import React, { useEffect, useMemo, useRef, useState } from "react";
import { Container, Row, Col, Form, Button, Card, Modal, Badge } from "react-bootstrap";
import Select from "react-select";
import { ToastContainer, toast } from "react-toastify";
import axios from "axios";
import * as htmlToImage from "html-to-image";

const sampleSchoolLogo = `${process.env.PUBLIC_URL}/assets/sampleSchoolLogo.png`;

const IdCardGenerator = () => {
    const userData = sessionStorage.getItem("user");
    const userObj = userData ? JSON.parse(userData) : {};
    const baseUrl = process.env.REACT_APP_API_BASE_URL;
    const [classes, setClasses] = useState([]);
    const [sections, setSections] = useState([]);
    const [students, setStudents] = useState([]);
    const [school, setSchool] = useState(null);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [form, setForm] = useState({
        class_id: "",
        section_id: "",
        student_id: "",
    });
    const [loading, setLoading] = useState(false);
    const [showCardSection, setShowCardSection] = useState(false);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [showField, setShowField] = useState({
        father_name: false,
        mother_name: false,
        father_mobile: true,
        mother_mobile: false,
        blood_group: false,
        address: true,
        class_teacher_name: false,
        class_teacher_number: false,
    });

    const toggle = (key) => setShowField((p) => ({ ...p, [key]: !p[key] }));

    const cardRef = useRef(null);

    const encodePath = (p = "") =>
        String(p)
            .replace(/^\/+/, "")
            .split("/")
            .map(encodeURIComponent)
            .join("/");

    const normalizeUrl = (pathOrUrl) => {
        if (!pathOrUrl) return "";
        const v = String(pathOrUrl).trim();
        if (!v) return "";

        if (v.startsWith("http://") || v.startsWith("https://") || v.startsWith("data:")) return v;

        const clean = v.replace(/^\/+/, "");

        return `${baseUrl}/uploads/get-image/${encodePath(clean)}`;
    };

    const logoUrlFromSession = userObj.logo ? normalizeUrl(userObj.logo) : sampleSchoolLogo;

    const toBase64FromUrl = async (url) => {
        const res = await axios.get(url, { responseType: "blob" });
        return await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(res.data);
        });
    };

    const waitForImages = async (rootEl) => {
        if (!rootEl) return;
        const imgs = Array.from(rootEl.querySelectorAll("img"));
        await Promise.all(
            imgs.map(
                (img) =>
                    new Promise((resolve) => {
                        if (img.complete && img.naturalWidth > 0) return resolve(true);
                        img.onload = () => resolve(true);
                        img.onerror = () => resolve(false);
                    })
            )
        );
    };

    const mandatoryOk = true;

    useEffect(() => {
        fetchClasses();
        fetchSchool();
    }, []);

    useEffect(() => {
        if (form.class_id) fetchSections(form.class_id);
        setForm((p) => ({ ...p, section_id: "", student_id: "" }));
        setSections([]);
        setStudents([]);
        setSelectedStudent(null);
        setShowCardSection(false);
    }, [form.class_id]);

    useEffect(() => {
        if (form.class_id && form.section_id) fetchStudents(form.class_id, form.section_id);
        setForm((p) => ({ ...p, student_id: "" }));
        setStudents([]);
        setSelectedStudent(null);
        setShowCardSection(false);
    }, [form.section_id]);

    const fetchClasses = async () => {
        try {
            const res = await axios.post(baseUrl + "/classes/", {
                action: "READ",
                school_id: userObj.school_id,
            });
            setClasses(res.data || []);
        } catch (err) {
            console.error("Error fetching classes:", err);
        }
    };

    const fetchSections = async (class_id) => {
        try {
            const res = await axios.post(baseUrl + "/Sections/", {
                action: "DROPDOWNREAD",
                class_id,
                school_id: userObj.school_id,
                academic_year_id: userObj.academic_year_id,
            });
            setSections(res.data || []);
        } catch (err) {
            console.error("Error fetching sections:", err);
        }
    };

    const fetchSchool = async () => {
        try {
            const res = await axios.post(baseUrl + "/schoolmaster/", {
                action: "FILTER",
                school_id: userObj.school_id,
            });

            const row = Array.isArray(res.data)
                ? res.data.find((x) => String(x.school_id) === String(userObj.school_id)) || null
                : null;

            let finalLogo = logoUrlFromSession;

            try {
                if (finalLogo && finalLogo !== sampleSchoolLogo && !finalLogo.startsWith("data:")) {
                    finalLogo = await toBase64FromUrl(finalLogo);
                }
            } catch (e) {
                console.warn("Logo base64 conversion failed:", e);
                finalLogo = logoUrlFromSession;
            }

            setSchool({
                school_name: row?.school_name || userObj.school_name || "",
                logo: finalLogo,
                phone: row?.school_office_phone_number || row?.mobile_number || "",
                email: row?.email || "",
            });
        } catch (err) {
            console.error("School fetch error:", err);

            setSchool({
                school_name: userObj.school_name || "",
                logo: logoUrlFromSession,
                phone: "",
                email: "",
            });
        }
    };

    const fetchStudents = async (class_id, section_id) => {
        try {
            setLoading(true);

            const res = await axios.post(baseUrl + "/students/", {
                action: "FILTER",
                school_id: userObj.school_id,
                academic_year_id: userObj.academic_year_id,
                class_id,
                section_id,
            });

            const data = Array.isArray(res.data) ? res.data : [];
            setStudents(data);
        } catch (err) {
            console.error("Students fetch error:", err);
            setStudents([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchTeacherPhone = async (teacherEmpId) => {
        if (!teacherEmpId) return "";
        try {
            const res = await axios.post(baseUrl + "/employees/", {
                action: "FILTER",
                emp_id: Number(teacherEmpId),
                school_id: Number(userObj.school_id),
                status: "A",
            });

            const row = Array.isArray(res.data) ? res.data[0] : null;

            return row?.phonemumber || row?.phonenumber || "";
        } catch (e) {
            console.warn("Teacher phone fetch failed:", e);
            return "";
        }
    };

    const studentOptions = useMemo(() => {
        return (students || []).map((st) => ({
            value: st.student_id,
            label: `${st.student_first_name || ""} ${st.student_last_name || ""}`,
            data: st,
        }));
    }, [students]);

    const handleInputChange = (e) => {
        const { id, value } = e.target;
        setForm((p) => ({ ...p, [id]: value ?? "" }));
    };

    const handleStudentSelect = async (opt) => {
        if (!opt) {
            setForm((p) => ({ ...p, student_id: "" }));
            setSelectedStudent(null);
            setShowCardSection(false);
            return;
        }

        setForm((p) => ({ ...p, student_id: opt.value }));

        const st = opt.data || {};

        const photoUrl = normalizeUrl(st.passport_size_photo || st.image_id);

        let photo64 = photoUrl;
        try {
            if (photoUrl && !photoUrl.startsWith("data:")) photo64 = await toBase64FromUrl(photoUrl);
        } catch (e) {
            console.warn("Student photo base64 conversion failed:", e);
        }

        setSelectedStudent({
            student_id: st.student_id,
            student_name: `${st.student_first_name || ""} ${st.student_last_name || ""}`.trim(),
            class_name: st.class_name || "",
            section_name: st.section_name || "",
            roll_no: st.roll_no || "",
            reg_no: st.admission_number || "",
            photo: photo64,

            father_name: `${st.father_firstname || ""} ${st.father_surname || ""}`.trim(),
            mother_name: `${st.mother_firstname || ""} ${st.mother_surname || ""}`.trim(),
            father_mobile: st.father_phone_number || "",
            mother_mobile: st.mother_phone_number || "",

            blood_group: st.blood_group_name || "",
            address: st.address || st.permanent_address || "",

            class_teacher_name: (st.student_class_teacher_name || "").trim(),
            class_teacher_number: "",
        });

        const teacherPhone = await fetchTeacherPhone(st.student_class_teacher_id);
        setSelectedStudent((prev) => ({
            ...prev,
            class_teacher_number: teacherPhone || "",
        }));
    };

    const handleSearch = (e) => {
        e.preventDefault();
        if (!form.class_id) return toast.error("Class is required");
        if (!form.section_id) return toast.error("Section is required");
        if (!form.student_id) return toast.error("Student is required");
        setShowCardSection(true);
    };

    const handleReset = () => {
        setForm({ class_id: "", section_id: "", student_id: "" });
        setSections([]);
        setStudents([]);
        setSelectedStudent(null);
        setShowCardSection(false);
        setShowField({
            father_name: false,
            mother_name: false,
            father_mobile: true,
            mother_mobile: false,
            blood_group: false,
            address: true,
            class_teacher_name: false,
            class_teacher_number: false,
        });
    };

    const downloadPng = async () => {
        try {
            await waitForImages(cardRef.current);

            const dataUrl = await htmlToImage.toPng(cardRef.current, {
                cacheBust: true,
                pixelRatio: 3,
                backgroundColor: "#ffffff",
            });

            const link = document.createElement("a");
            link.download = `${(selectedStudent?.student_name || "ID_CARD").replaceAll(" ", "_")}.png`;
            link.href = dataUrl;
            link.click();
        } catch (err) {
            console.error("downloadPng error:", err);
            toast.error("Download failed. Check image URL / CORS.");
        }
    };

    return (
        <div className="pageBody">
            <ToastContainer />
            <Container fluid>
                <Card>
                    <Card.Body className="hide-scrollbar">
                        <form onSubmit={handleSearch}>
                            <Row>
                                <Col xs={12}>
                                    <h6 className="commonSectionTitle">ID Card Generator</h6>
                                </Col>
                            </Row>

                            <Row>
                                <Col xs={12} md={6} lg={3}>
                                    <Form.Group className="commonInput">
                                        <Form.Label>
                                            Class<span className="requiredStar">*</span>
                                        </Form.Label>
                                        <Form.Select id="class_id" value={form.class_id} onChange={handleInputChange} required>
                                            <option value="" disabled hidden>
                                                Select Class
                                            </option>
                                            {classes.map((cls) => (
                                                <option key={cls.class_id} value={cls.class_id}>
                                                    {cls.class_name}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>

                                <Col xs={12} md={6} lg={3}>
                                    <Form.Group className="commonInput">
                                        <Form.Label>
                                            Section<span className="requiredStar">*</span>
                                        </Form.Label>
                                        <Form.Select
                                            id="section_id"
                                            value={form.section_id}
                                            onChange={handleInputChange}
                                            required
                                            disabled={!form.class_id}
                                        >
                                            <option value="" disabled hidden>
                                                Select Section
                                            </option>
                                            {sections.map((sec) => (
                                                <option key={sec.section_id} value={sec.section_id}>
                                                    {sec.section_name}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>

                                <Col xs={12} md={12} lg={4}>
                                    <Form.Group className="commonInput">
                                        <Form.Label>
                                            Student<span className="requiredStar">*</span>
                                        </Form.Label>
                                        <Select
                                            classNamePrefix="react-select"
                                            isClearable
                                            isSearchable
                                            isLoading={loading}
                                            placeholder={form.section_id ? "Search student..." : "Select Class & Section first"}
                                            options={studentOptions}
                                            onChange={handleStudentSelect}
                                            value={studentOptions.find((o) => String(o.value) === String(form.student_id)) || null}
                                            isDisabled={!form.section_id}
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                            menuShouldScrollIntoView={false}
                                            styles={{
                                                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                                menu: (base) => ({ ...base, zIndex: 9999 }),
                                            }}
                                        />
                                    </Form.Group>
                                </Col>

                                <Col xs={12} lg={2}>
                                    <div className="d-flex justify-content-end mt-4">
                                        <Button type="button" variant="primary" className="btn-info clearBtn me-3" onClick={handleReset}>
                                            Reset
                                        </Button>
                                        <Button type="submit" variant="primary" className="btn-success primaryBtn">
                                            Search
                                        </Button>
                                    </div>
                                </Col>
                            </Row>
                        </form>
                    </Card.Body>
                </Card>

                {showCardSection && (
                    <Row className="mt-3 g-3">
                        <Col xs={12} lg={5}>
                            <Card>
                                <Card.Body className="hide-scrollbar">
                                    <h6 className="commonSectionTitle">Customize</h6>
                                    <hr />

                                    <Row className="g-2">
                                        <Col xs={6}>
                                            <Form.Check
                                                type="checkbox"
                                                label="Father Name"
                                                checked={showField.father_name}
                                                onChange={() => toggle("father_name")}
                                            />
                                        </Col>
                                        <Col xs={6}>
                                            <Form.Check
                                                type="checkbox"
                                                label="Mother Name"
                                                checked={showField.mother_name}
                                                onChange={() => toggle("mother_name")}
                                            />
                                        </Col>

                                        <Col xs={6}>
                                            <Form.Check
                                                type="checkbox"
                                                label="Father Mobile"
                                                checked={showField.father_mobile}
                                                onChange={() => toggle("father_mobile")}
                                            />
                                        </Col>
                                        <Col xs={6}>
                                            <Form.Check
                                                type="checkbox"
                                                label="Mother Mobile"
                                                checked={showField.mother_mobile}
                                                onChange={() => toggle("mother_mobile")}
                                            />
                                        </Col>

                                        <Col xs={6}>
                                            <Form.Check
                                                type="checkbox"
                                                label="Blood Group"
                                                checked={showField.blood_group}
                                                onChange={() => toggle("blood_group")}
                                            />
                                        </Col>
                                        <Col xs={6}>
                                            <Form.Check
                                                type="checkbox"
                                                label="Address"
                                                checked={showField.address}
                                                onChange={() => toggle("address")}
                                            />
                                        </Col>

                                        <Col xs={6}>
                                            <Form.Check
                                                type="checkbox"
                                                label="Class Teacher Name"
                                                checked={showField.class_teacher_name}
                                                onChange={() => toggle("class_teacher_name")}
                                            />
                                        </Col>
                                        <Col xs={6}>
                                            <Form.Check
                                                type="checkbox"
                                                label="Class Teacher Number"
                                                checked={showField.class_teacher_number}
                                                onChange={() => toggle("class_teacher_number")}
                                            />
                                        </Col>
                                    </Row>

                                    <div className="d-flex justify-content-end mt-4">
                                        <Button variant="secondary" className="me-2" onClick={() => setIsFullScreen(true)}>
                                            Full Screen
                                        </Button>
                                        <Button variant="success" className="primaryBtn" onClick={downloadPng}>
                                            Download PNG
                                        </Button>
                                    </div>

                                    {!mandatoryOk && (
                                        <div style={{ marginTop: 10 }}>
                                            <Badge bg="danger">Mandatory data missing</Badge>
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col xs={12} lg={7}>
                            <Card>
                                <Card.Body className="hide-scrollbar">
                                    <h6 className="commonSectionTitle">ID Card Preview</h6>
                                    <div style={{ display: "flex", justifyContent: "center", padding: 14 }}>
                                        <IdCard ref={cardRef} school={school} student={selectedStudent} showField={showField} />
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                )}
            </Container>

            <Modal show={isFullScreen} onHide={() => setIsFullScreen(false)} fullscreen>
                <Modal.Header closeButton>
                    <Modal.Title>ID Card - Full Screen</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                    <div>
                        <div style={{ display: "flex", justifyContent: "center" }}>
                            <IdCard school={school} student={selectedStudent} showField={showField} />
                        </div>
                        <div className="d-flex justify-content-center mt-3">
                            <Button variant="success" onClick={downloadPng}>
                                Download PNG
                            </Button>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default IdCardGenerator;

const IdCard = React.forwardRef(({ school, student, showField }, ref) => {
    const s = school || {};
    const st = student || {};
    const safe = (v) => (v ?? "").toString();

    const logoSrc = safe(s.logo);
    const photoSrc = safe(st.photo);
    const calcNameFontSize = (name = "") => {
        const n = String(name || "").trim().length;
        if (n <= 14) return 22;
        if (n <= 22) return 19;
        if (n <= 30) return 16;
        return 14;
    };

    const calcLineHeight = (fontSize) => Math.max(16, Math.round(fontSize * 1.15));

    const visibleDetailsCount = (() => {
        const rows = [
            showField.father_name && st.father_name,
            showField.mother_name && st.mother_name,
            showField.father_mobile && st.father_mobile,
            showField.mother_mobile && st.mother_mobile,
            showField.blood_group && st.blood_group,
            showField.address && st.address,
            showField.class_teacher_name && st.class_teacher_name,
            showField.class_teacher_number && st.class_teacher_number,
        ];
        return rows.filter(Boolean).length;
    })();


    return (
        <div ref={ref} style={styles.cardOuter}>
            <div style={styles.bgTopLeft} />
            <div style={styles.bgTopLeftSolid} />
            <div style={styles.bgBottomRight} />
            <div style={styles.bgStripeBand} />
            <div style={styles.bgDots} />

            <div style={styles.logoWrap}>
                {logoSrc ? <img src={logoSrc} alt="logo" style={styles.logo} /> : <div style={styles.logoFallback}>LOGO</div>}
            </div>

            <div style={styles.photoCircle}>
                {photoSrc ? <img src={photoSrc} alt="student" style={styles.photo} /> : <div style={styles.photoFallback}>PHOTO</div>}
            </div>

            {/* <div style={styles.regRibbon}>
                <div style={styles.regRibbonText}>Reg. No : {safe(st.reg_no)}</div>
            </div> */}

            <div
                style={{
                    ...styles.nameText,
                    fontSize: calcNameFontSize(st.student_name),
                    lineHeight: `${calcLineHeight(calcNameFontSize(st.student_name))}px`,
                    fontWeight: 600, // ✅ not bold
                    padding: "0 14px",
                    wordBreak: "break-word",
                    overflowWrap: "anywhere",
                    whiteSpace: "normal",
                }}
            >
                {safe(st.student_name)}
            </div>

            <div
                style={{
                    ...styles.classText,
                    fontWeight: 600, // ✅ not bold
                    padding: "0 14px",
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    overflowWrap: "anywhere",
                }}
            >
                CLASS - {safe(st.class_name)} &nbsp; | &nbsp; SEC - {safe(st.section_name)} &nbsp; | &nbsp; ROLL - {safe(st.roll_no)}
            </div>

            <div style={styles.detailsWrap}>
                {showField.father_name && st.father_name && <DetailRow label="Father Name" value={safe(st.father_name)} />}
                {showField.mother_name && st.mother_name && <DetailRow label="Mother Name" value={safe(st.mother_name)} />}

                {showField.father_mobile && st.father_mobile && <DetailRow label="Father Mobile" value={safe(st.father_mobile)} />}
                {showField.mother_mobile && st.mother_mobile && <DetailRow label="Mother Mobile" value={safe(st.mother_mobile)} />}

                {showField.blood_group && st.blood_group && <DetailRow label="Blood Group" value={safe(st.blood_group)} />}
                {showField.address && st.address && <DetailRow label="Address" value={safe(st.address)} />}

                {showField.class_teacher_name && st.class_teacher_name && <DetailRow label="Class Teacher" value={safe(st.class_teacher_name)} />}
                {showField.class_teacher_number && st.class_teacher_number && <DetailRow label="Teacher Mobile" value={safe(st.class_teacher_number)} />}
            </div>

            <div style={styles.footerWrap}>
                <div style={styles.footerSchool}>{safe(s.school_name)}</div>
                <div style={styles.footerRow}>
                    <div style={styles.footerItem}>📞 {safe(s.phone)}</div>
                    <div style={styles.footerItem}>✉️ {safe(s.email)}</div>
                </div>
            </div>
        </div>
    );
});

function DetailRow({ label, value }) {
    if (!value) return null;
    return (
        <div style={styles.detailRow}>
            <div style={styles.detailBullet} />
            <div style={styles.detailLabel}>{label}</div>
            <div style={styles.detailDash}>-</div>
            <div style={styles.detailValue}>{value}</div>
        </div>
    );
}

const styles = {
    primary: "#0461FE",
    cardOuter: {
        width: 360,
        height: 520,
        background: "#fff",
        borderRadius: 18,
        position: "relative",
        overflow: "hidden",
        border: "1px solid rgba(0,0,0,0.06)",
        boxShadow: "0 10px 30px rgba(0,0,0,0.10)",
        fontFamily: "Arial, sans-serif",
    },
    bgTopLeft: {
        position: "absolute",
        top: -70,
        left: -80,
        width: 240,
        height: 240,
        background: "rgba(4,97,254,0.12)",
        borderRadius: 80,
        transform: "rotate(18deg)",
    },
    bgTopLeftSolid: {
        position: "absolute",
        top: -40,
        left: -30,
        width: 150,
        height: 150,
        background: "rgba(4,97,254,0.20)",
        borderRadius: 50,
        transform: "rotate(-10deg)",
    },
    bgBottomRight: {
        position: "absolute",
        bottom: -90,
        right: -90,
        width: 260,
        height: 260,
        background: "rgba(4,97,254,0.10)",
        borderRadius: 90,
        transform: "rotate(-12deg)",
    },
    bgStripeBand: {
        position: "absolute",
        top: 120,
        right: -70,
        width: 240,
        height: 52,
        transform: "rotate(18deg)",
        background: "linear-gradient(90deg, rgba(4,97,254,0.00) 0%, rgba(4,97,254,0.12) 35%, rgba(4,97,254,0.00) 100%)",
        borderRadius: 18,
    },
    bgDots: {
        position: "absolute",
        top: 22,
        right: 18,
        width: 120,
        height: 70,
        backgroundImage: "radial-gradient(rgba(4,97,254,0.35) 1.2px, transparent 1.2px)",
        backgroundSize: "10px 10px",
        opacity: 0.8,
        borderRadius: 12,
    },
    logoWrap: {
        position: "absolute",
        top: 16,
        left: 16,
        width: 62,
        height: 62,
        borderRadius: 14,
        background: "transparent", // ✅ removed white bg
        display: "grid",
        placeItems: "center",
        border: "none",            // ✅ removed border
        zIndex: 2,
    },

    logo: { width: 44, height: 44, objectFit: "contain" },
    logoFallback: { fontSize: 10, fontWeight: 800, color: "#0B1F4A" },
    photoCircle: {
        position: "absolute",
        top: 50,
        left: "50%",
        transform: "translateX(-50%)",
        width: 170,
        height: 170,
        borderRadius: "50%",
        border: "12px solid rgba(4,97,254,0.95)",
        background: "#fff",
        display: "grid",
        placeItems: "center",
        zIndex: 2,
        boxShadow: "0 8px 20px rgba(4,97,254,0.18)",
    },
    photo: {
        width: 150,
        height: 150,
        borderRadius: "50%",
        objectFit: "cover",
        border: "4px solid #fff",
    },
    photoFallback: { fontSize: 10, fontWeight: 800, color: "#0B1F4A" },
    regRibbon: {
        position: "absolute",
        top: 86,
        left: "calc(50% + 70px)",
        width: 66,
        height: 150,
        background: "#0461FE",
        borderTopLeftRadius: 40,
        borderBottomLeftRadius: 40,
        transform: "rotate(12deg)",
        display: "grid",
        placeItems: "center",
        zIndex: 3,
        boxShadow: "0 10px 18px rgba(4,97,254,0.22)",
    },
    regRibbonText: {
        transform: "rotate(90deg)",
        fontWeight: 900,
        color: "#fff",
        fontSize: 12,
        letterSpacing: 0.2,
    },
    nameText: {
        position: "absolute",
        top: 245,
        width: "100%",
        textAlign: "center",
        fontSize: 26,
        fontWeight: 900,
        color: "#0B1F4A",
        zIndex: 2,
    },
    classText: {
        position: "absolute",
        top: 282,
        width: "100%",
        textAlign: "center",
        fontSize: 13,
        fontWeight: 900,
        color: "#0461FE",
        zIndex: 2,
    },
    detailsWrap: {
        position: "absolute",
        top: 320,
        left: 18,
        right: 18,
        zIndex: 2,
    },
    detailRow: {
        display: "grid",
        gridTemplateColumns: "14px 120px 14px 1fr",
        gap: 6,
        marginBottom: 8,
        fontSize: 13,
        color: "#253858",
        fontWeight: 700,
    },
    detailBullet: {
        width: 10,
        height: 10,
        background: "#0461FE",
        borderRadius: 3,
        marginTop: 3,
    },
    detailLabel: { color: "#253858" },
    detailDash: { textAlign: "center", color: "#253858" },
    detailValue: { color: "#253858", fontWeight: 800 },
    footerWrap: {
        position: "absolute",
        left: 0,
        right: 0,
        bottom: 0,
        padding: "14px 14px 12px 14px",
        borderTop: "4px solid #0461FE",
        background: "rgba(255,255,255,0.96)",
        zIndex: 2,
    },
    footerSchool: {
        textAlign: "center",
        fontWeight: 900,
        color: "#0B1F4A",
        fontSize: 20,
    },
    footerRow: {
        marginTop: 6,
        display: "flex",
        justifyContent: "space-between",
        gap: 10,
        fontSize: 12,
        color: "#253858",
        fontWeight: 700,
    },
    footerItem: {
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        maxWidth: "48%",
    },
};
